import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  TextField, Button, Typography, Box, Alert, Table,
  TableHead, TableRow, TableCell, TableBody, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  Chip, FormHelperText, MenuItem, Grid
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { API_URL } from './constants';
import { useAuth } from './AuthContext';


const SupportView = () => {
  const { projectKey } = useParams();
  const { projectKey: authProjectKey } = useAuth();
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [message, setMessage] = useState('');
  const [visibility, setVisibility] = useState('shared'); // 'shared' or 'private'
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [issues, setIssues] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editTicketId, setEditTicketId] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editExpectedBehavior, setEditExpectedBehavior] = useState('');
  const [editActualBehavior, setEditActualBehavior] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editFeedback, setEditFeedback] = useState('');
  const [editFeedbackType, setEditFeedbackType] = useState('success');
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  const currentProjectKey = projectKey || authProjectKey;

  // If project_key is None or empty, force private visibility
  useEffect(() => {
    if (!currentProjectKey) {
      setVisibility('private');
    }
  }, [currentProjectKey]);

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open') return 'warning'; // Yellow
    if (statusLower === 'in progress') return 'info'; // Blue
    if (statusLower === 'waiting for user') return 'default'; // Gray
    if (statusLower === 'resolved') return 'success'; // Green
    if (statusLower === 'closed') return 'default'; // White/Gray
    return 'default'; // Default fallback
  };

  // Helper function to get category emoji
  const getCategoryEmoji = (category) => {
    const categoryMap = {
      'Bug': '⚠️',
      'Feature request': '🛠️',
      'Question': '❓',
      'Other': '📌'
    };
    return categoryMap[category] || '📌';
  };

  /** Format time estimate: calculate day/hour range from completion dates. */
  const formatTimeEstimate = (eta, opts = {}) => {
    const status = opts.status;
    const statusLower = (status || '').toLowerCase();
    
    // If resolved or closed, show "-"
    if (statusLower === 'resolved' || statusLower === 'closed') {
      return '-';
    }
    
    const earliestDate = opts.earliest_completion_date;
    const latestDate = opts.latest_completion_date;
    
    if (!earliestDate && !latestDate) {
      if (eta == null || eta === '') return 'Not set';
      const date = new Date(eta);
      if (isNaN(date.getTime())) return 'Not set';
      const now = new Date();
      const diffMs = date - now;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      // If overdue (negative hours), show "due X hours"
      if (diffHours < 0) {
        const hoursOverdue = Math.abs(Math.floor(diffHours));
        return `due ${hoursOverdue} hours`;
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      const diffDaysMs = date - today;
      const days = Math.ceil(diffDaysMs / (24 * 60 * 60 * 1000));
      if (days === 0) return 'Today';
      if (days === 1) return 'in 1 day';
      return `in ${days} days`;
    }
    
    try {
      const now = new Date();
      let earliest, latest;
      
      if (earliestDate) {
        earliest = new Date(earliestDate);
      } else {
        return 'Not set';
      }
      
      if (latestDate) {
        latest = new Date(latestDate);
      } else {
        latest = earliest;
      }
      
      // Calculate hours from now to earliest and latest
      const earliestFromNowMs = earliest - now;
      const latestFromNowMs = latest - now;
      const earliestFromNowHours = earliestFromNowMs / (1000 * 60 * 60);
      const latestFromNowHours = latestFromNowMs / (1000 * 60 * 60);
      
      // If overdue (negative hours) and not resolved/closed, show "due X hours"
      if (earliestFromNowHours < 0) {
        const hoursOverdue = Math.abs(Math.floor(earliestFromNowHours));
        return `due ${hoursOverdue} hours`;
      }
      
      // If less than 24 hours remaining, show hours range (0-23 hours)
      if (earliestFromNowHours < 24 && earliestFromNowHours >= 0) {
        const minHours = Math.max(0, Math.floor(earliestFromNowHours));
        const maxHours = Math.min(23, Math.ceil(latestFromNowHours));
        if (minHours === maxHours) {
          return `in ${minHours} hours`;
        }
        return `in ${minHours} to ${maxHours} hours`;
      }
      
      // Calculate calendar days from now
      const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const earliestDateOnly = new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
      const latestDateOnly = new Date(latest.getFullYear(), latest.getMonth(), latest.getDate());
      const earliestDays = Math.ceil((earliestDateOnly - nowDateOnly) / (1000 * 60 * 60 * 24));
      const latestDays = Math.ceil((latestDateOnly - nowDateOnly) / (1000 * 60 * 60 * 24));
      
      if (earliestDays < 0) {
        // Calculate hours overdue
        const hoursOverdue = Math.abs(Math.floor(earliestFromNowHours));
        return `due ${hoursOverdue} hours`;
      }
      if (earliestDays === latestDays) {
        if (earliestDays === 0) return 'Today';
        if (earliestDays === 1) return 'in 1 day';
        return `in ${earliestDays} days`;
      }
      return `in ${earliestDays} to ${latestDays} days`;
    } catch {
      return 'Not set';
    }
  };

const fetchIssues = async () => {
    try {
      const res = await axios.get(`${API_URL}/support`, {
        withCredentials: true
      });
      setIssues(res.data);
    } catch (error) {
      console.error('Error fetching support issues:', error);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [location.pathname]);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
    
    const validFiles = [];
    const errors = [];
    
    selectedFiles.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} exceeds 25 MB limit`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (errors.length > 0) {
      setUploadError(errors.join(', '));
    } else {
      setUploadError('');
    }
    
    setFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
    const imageFiles = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        if (blob && blob.size <= MAX_FILE_SIZE) {
          const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
          imageFiles.push(file);
        } else if (blob && blob.size > MAX_FILE_SIZE) {
          setUploadError('Pasted image exceeds 25 MB limit');
        }
      }
    }
    
    if (imageFiles.length > 0) {
      setFiles(prev => [...prev, ...imageFiles]);
      setUploadError('');
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    const isBug = category === 'Bug';
    const hasRequiredForBug = isBug && expectedBehavior && actualBehavior;
    const hasRequiredForOther = !isBug && message;
    if (!category || !subject || (!hasRequiredForBug && !hasRequiredForOther)) return;

    try {
      // Build the message: for Bug use only bug fields; for other categories use "Describe the issue"
      let fullMessage;
      if (isBug) {
        const bugDetails = [];
        if (expectedBehavior) {
          bugDetails.push(`What I did and what I expected to happen:\n${expectedBehavior}`);
        }
        if (actualBehavior) {
          bugDetails.push(`What actually happened:\n${actualBehavior}`);
        }
        fullMessage = bugDetails.join('\n\n');
      } else {
        fullMessage = message;
      }

      // Create the ticket with the selected category as type
      const ticketResponse = await axios.post(
        `${API_URL}/support`,
        { 
          subject, 
          type: category || 'Other', 
          message: fullMessage,
          is_private: visibility === 'private'
        },
        { withCredentials: true }
      );
      
      const ticketId = ticketResponse.data.id;
      
      // Upload files if any
      if (files.length > 0) {
        const uploadPromises = files.map(file => {
          const formData = new FormData();
          formData.append('file', file);
          return axios.post(
            `${API_URL}/support/${ticketId}/attachments`,
            formData,
            {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
        });
        
        try {
          await Promise.all(uploadPromises);
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          // Ticket is created, but files failed to upload
        }
      }
      
      setSubmitted(true);
      setCategory('');
      setSubject('');
      setExpectedBehavior('');
      setActualBehavior('');
      setMessage('');
      setVisibility('shared');
      setFiles([]);
      fetchIssues(); // Refresh list
      
      // Navigate to ticket detail page
      setTimeout(() => {
        const basePath = currentProjectKey ? `/${currentProjectKey}/support` : '/support';
        navigate(`${basePath}/${ticketId}`);
      }, 1000);
    } catch (error) {
      console.error('Error submitting support issue:', error);
      setUploadError('Failed to submit ticket. Please try again.');
    }
  };

  const handleDelete = async (ticketId) => {
    try {
      await axios.delete(`${API_URL}/support/${ticketId}`, {
        withCredentials: true
      });
      fetchIssues();
    } catch (error) {
      console.error('Error deleting support issue:', error);
    }
  };

  const handleEdit = (ticketId) => {
    const ticket = issues.find((issue) => issue.id === ticketId);
    if (!ticket) return;
    const msg = ticket.message || '';
    setEditTicketId(ticketId);
    setEditSubject(ticket.subject || '');
    setEditMessage(msg);
    // Try to parse bug format to pre-fill when editing
    const actualMarker = 'What actually happened:\n';
    const idx = msg.indexOf(actualMarker);
    if (idx >= 0) {
      setEditCategory('Bug');
      setEditActualBehavior(msg.slice(idx + actualMarker.length).trim());
      const before = msg.slice(0, idx).trim();
      const expectedContent = before
        .replace(/^What I did and what I expected to happen:\n?/i, '')
        .replace(/^What I expected to happen:\n?/i, '');
      setEditExpectedBehavior(expectedContent.trim());
    } else {
      // Default to 'Other' if we can't determine the category
      setEditCategory('Other');
      setEditExpectedBehavior('');
      setEditActualBehavior('');
    }
    setEditOpen(true);
  };

  const handleCloseEdit = (force = false) => {
    if (editSaving && !force) return;
    setEditOpen(false);
    setEditTicketId(null);
    setEditCategory('');
    setEditSubject('');
    setEditExpectedBehavior('');
    setEditActualBehavior('');
    setEditMessage('');
  };

  const handleUpdate = async () => {
    const isEditBug = editCategory === 'Bug';
    const hasRequiredForBug = isEditBug && editExpectedBehavior && editActualBehavior;
    const hasRequiredForOther = !isEditBug && editMessage;
    if (!editTicketId || !editSubject || (!hasRequiredForBug && !hasRequiredForOther)) return;
    setEditSaving(true);
    try {
      // Build the message: for Bug use only bug fields; for other categories use "Describe the issue"
      let fullMessage;
      if (isEditBug) {
        const bugDetails = [];
        if (editExpectedBehavior) {
          bugDetails.push(`What I did and what I expected to happen:\n${editExpectedBehavior}`);
        }
        if (editActualBehavior) {
          bugDetails.push(`What actually happened:\n${editActualBehavior}`);
        }
        fullMessage = bugDetails.join('\n\n');
      } else {
        fullMessage = editMessage;
      }

      // Keep existing type, don't update it
      await axios.put(
        `${API_URL}/support/${editTicketId}`,
        { subject: editSubject, message: fullMessage },
        { withCredentials: true }
      );
      setEditFeedback('Ticket updated.');
      setEditFeedbackType('success');
      handleCloseEdit(true);
      fetchIssues();
    } catch (error) {
      console.error('Error updating support issue:', error);
      setEditFeedback('Failed to update ticket.');
      setEditFeedbackType('error');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Support / Report an Issue
      </Typography>

      {submitted && <Alert severity="success" sx={{ mb: 2 }}>Thanks! We've received your message.</Alert>}
      {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}

      <Grid container spacing={3}>
        {/* Left Column: New Ticket Form */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Create New Ticket
            </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
          Category <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {[
            { value: 'Bug', label: 'Bug', emoji: '⚠️' },
            { value: 'Feature request', label: 'Feature request', emoji: '🛠️' },
            { value: 'Question', label: 'Question', emoji: '❓' },
            { value: 'Other', label: 'Other', emoji: '📌' }
          ].map((cat) => (
            <Box
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              sx={{
                flex: '1 1 0',
                minWidth: 120,
                px: 2.5,
                py: 1.5,
                border: '2px solid',
                borderColor: category === cat.value ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                backgroundColor: category === cat.value ? 'rgba(25, 118, 210, 0.08)' : 'white',
                color: category === cat.value ? 'primary.main' : 'text.primary',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                fontWeight: category === cat.value ? 600 : 400,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: category === cat.value ? 'rgba(25, 118, 210, 0.12)' : 'grey.50'
                }
              }}
            >
              <Typography variant="body2">
                {cat.emoji} {cat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <TextField
        label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        fullWidth
        margin="normal"
        required
        placeholder="e.g., Wrong data in the dataset"
        helperText="Be specific: describe what's wrong or what you need help with"
      />

      {category === 'Bug' && (
        <>
          <TextField
            label="What I did and what I expected to happen"
            value={expectedBehavior}
            onChange={(e) => setExpectedBehavior(e.target.value)}
            multiline
            rows={5}
            fullWidth
            margin="normal"
            required
            onPaste={handlePaste}
            inputRef={messageInputRef}
            placeholder="Describe the steps you took and what you expected to see or happen..."
            helperText="Include steps to reproduce and the expected outcome"
          />
          <TextField
            label="What actually happened"
            value={actualBehavior}
            onChange={(e) => setActualBehavior(e.target.value)}
            onPaste={handlePaste}
            multiline
            rows={4}
            fullWidth
            margin="normal"
            required
            placeholder="Describe what actually happened instead... (Press CTRL+V to paste images)"
          />
        </>
      )}

      {category !== 'Bug' && (
        <TextField
          label="Describe the issue"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onPaste={handlePaste}
          inputRef={messageInputRef}
          multiline
          rows={4}
          fullWidth
          margin="normal"
          required
          placeholder="Describe the issue... (Press CTRL+V to paste images)"
        />
      )}

      <Box sx={{ mt: 2, mb: 2 }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id="file-upload"
        />
        <label htmlFor="file-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<AttachFileIcon />}
            sx={{ mr: 1 }}
          >
            Attach Files
          </Button>
        </label>
        <FormHelperText sx={{ mt: 1 }}>
          Maximum file size: 25 MB. You can also paste images using CTRL+V.
        </FormHelperText>
      </Box>

      {files.length > 0 && (
        <Box sx={{ mt: 1, mb: 2 }}>
          {files.map((file, index) => (
            <Chip
              key={index}
              label={`${file.name} (${formatFileSize(file.size)})`}
              onDelete={() => removeFile(index)}
              deleteIcon={<DeleteIcon />}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}

      {/* Visibility selector */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Visibility
        </Typography>
        {!currentProjectKey ? (
          // When no project_key, show only private option (disabled)
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Box
              sx={{
                flex: '1 1 0',
                minWidth: 140,
                px: 1.5,
                py: 1,
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                color: 'primary.main',
                textAlign: 'center',
                fontWeight: 600,
                opacity: 0.7
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <LockIcon fontSize="small" sx={{ fontSize: 16 }} />
                Private
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
                only you & support
              </Typography>
            </Box>
          </Box>
        ) : (
          // When project_key exists, show both options
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Box
              onClick={() => setVisibility('shared')}
              sx={{
                flex: '1 1 0',
                minWidth: 140,
                px: 1.5,
                py: 1,
                border: '2px solid',
                borderColor: visibility === 'shared' ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                backgroundColor: visibility === 'shared' ? 'rgba(25, 118, 210, 0.08)' : 'white',
                color: visibility === 'shared' ? 'primary.main' : 'text.primary',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                fontWeight: visibility === 'shared' ? 600 : 400,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: visibility === 'shared' ? 'rgba(25, 118, 210, 0.12)' : 'grey.50'
                }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                Shared with team
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
                your team & support
              </Typography>
            </Box>
            <Box
              onClick={() => setVisibility('private')}
              sx={{
                flex: '1 1 0',
                minWidth: 140,
                px: 1.5,
                py: 1,
                border: '2px solid',
                borderColor: visibility === 'private' ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                backgroundColor: visibility === 'private' ? 'rgba(25, 118, 210, 0.08)' : 'white',
                color: visibility === 'private' ? 'primary.main' : 'text.primary',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                fontWeight: visibility === 'private' ? 600 : 400,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: visibility === 'private' ? 'rgba(25, 118, 210, 0.12)' : 'grey.50'
                }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <LockIcon fontSize="small" sx={{ fontSize: 16 }} />
                Private
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
                only you & support
              </Typography>
            </Box>
          </Box>
        )}
        {visibility === 'private' && (
          <FormHelperText sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
            <LockIcon fontSize="small" sx={{ fontSize: 12 }} />
            {!currentProjectKey 
              ? "Tickets without a project are automatically private and only visible to you and support staff."
              : "This ticket will only be visible to you and support staff."
            }
          </FormHelperText>
        )}
      </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={
                !category ||
                !subject ||
                (category === 'Bug' ? !(expectedBehavior && actualBehavior) : !message)
              }
              sx={{ mt: 2, width: '100%' }}
            >
              Submit
            </Button>
          </Paper>
        </Grid>

        {/* Right Column: Submitted Issues Table */}
        <Grid item xs={12} md={7}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Submitted Issues
          </Typography>

          <Paper>
            <Box sx={{ overflowX: 'auto', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Time estimate</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
            {issues.map((issue) => {
              const basePath = currentProjectKey ? `/${currentProjectKey}/support` : '/support';
              return (
              <TableRow 
                key={issue.id}
                hover
                onClick={() => navigate(`${basePath}/${issue.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Chip
                    label={`${getCategoryEmoji(issue.type || 'Other')} ${issue.type || 'Other'}`}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={issue.subject || '-'}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {issue.is_private && <LockIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />}
                    <Typography component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {issue.subject || '-'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={issue.status || 'Open'} 
                    size="small" 
                    color={getStatusColor(issue.status)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatTimeEstimate(issue.eta, {
                      earliest_completion_date: issue.earliest_completion_date,
                      latest_completion_date: issue.latest_completion_date,
                      status: issue.status
                    })}
                    size="small"
                    color={issue.earliest_completion_date || issue.latest_completion_date ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      const basePath = currentProjectKey ? `/${currentProjectKey}/support` : '/support';
                      navigate(`${basePath}/${issue.id}`);
                    }}
                    sx={{ mr: 1 }}
                  >
                    Show details
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(issue.id);
                    }}
                    aria-label="Edit support ticket"
                    disabled={!issue.can_delete}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {issue.can_delete ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(issue.id);
                      }}
                      aria-label="Delete support ticket"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </TableCell>
              </TableRow>
              );
            })}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit support ticket</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
              Category <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {[
                { value: 'Bug', label: 'Bug', emoji: '⚠️' },
                { value: 'Feature request', label: 'Feature request', emoji: '🛠️' },
                { value: 'Question', label: 'Question', emoji: '❓' },
                { value: 'Other', label: 'Other', emoji: '📌' }
              ].map((cat) => (
                <Box
                  key={cat.value}
                  onClick={() => setEditCategory(cat.value)}
                  sx={{
                    flex: '1 1 0',
                    minWidth: 100,
                    px: 2.5,
                    py: 1.5,
                    border: '2px solid',
                    borderColor: editCategory === cat.value ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    backgroundColor: editCategory === cat.value ? 'primary.light' : 'white',
                    color: editCategory === cat.value ? 'primary.main' : 'text.primary',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'center',
                    fontWeight: editCategory === cat.value ? 600 : 400,
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: editCategory === cat.value ? 'primary.light' : 'grey.50'
                    }
                  }}
                >
                  <Typography variant="body2">
                    {cat.emoji} {cat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <TextField
            label="Subject"
            value={editSubject}
            onChange={(e) => setEditSubject(e.target.value)}
            fullWidth
            margin="normal"
            required
            placeholder="e.g., Wrong data in the dataset"
            helperText="Be specific: describe what's wrong or what you need help with"
          />

          {editCategory === 'Bug' && (
            <>
              <TextField
                label="What I did and what I expected to happen"
                value={editExpectedBehavior}
                onChange={(e) => setEditExpectedBehavior(e.target.value)}
                multiline
                rows={5}
                fullWidth
                margin="normal"
                required
                placeholder="Describe the steps you took and what you expected to see or happen..."
                helperText="Include steps to reproduce and the expected outcome"
              />
              <TextField
                label="What actually happened"
                value={editActualBehavior}
                onChange={(e) => setEditActualBehavior(e.target.value)}
                onPaste={handlePaste}
                multiline
                rows={4}
                fullWidth
                margin="normal"
                required
                placeholder="Describe what actually happened instead... (Press CTRL+V to paste images)"
              />
            </>
          )}

          {editCategory !== 'Bug' && (
            <TextField
              label="Describe the issue"
              value={editMessage}
              onChange={(e) => setEditMessage(e.target.value)}
              multiline
              rows={4}
              fullWidth
              margin="normal"
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={
              editSaving ||
              !editCategory ||
              !editSubject ||
              (editCategory === 'Bug'
                ? !(editExpectedBehavior && editActualBehavior)
                : !editMessage)
            }
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!editFeedback}
        autoHideDuration={4000}
        onClose={() => setEditFeedback('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setEditFeedback('')}
          severity={editFeedbackType}
          sx={{ width: '100%' }}
        >
          {editFeedback}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportView;